<script lang="ts">
  import { humanizeLaunchType, humanizeOrbitType } from '$lib/utils/launch';
  import Button from '$lib/components/Button.svelte';
  import OrbitParameters from '$lib/components/Launch/OrbitParameters.svelte';
  import GeoChart from '$lib/components/GeoChart.svelte';
  import LaunchInfoCard from '$lib/components/Launch/LaunchInfoCard/LaunchInfoCard.svelte';
  import Carousel from '$lib/components/Carousel.svelte';
  import CarouselItem from '$lib/components/CarouselItem.svelte';
  import { formatDateFromString, getFormatTime } from '$lib/utils/localeFormatter';
  import Icon from '$lib/components/Icon.svelte';
  import LaunchCard from '$lib/components/Launch/LaunchCard.svelte';
  import cardLaunchSite from '$lib/assets/img/launchId/cardLaunchSite.png';
  import cardVehicle from '$lib/assets/img/launchId/cardVehicle.png';
  import { env } from '$env/dynamic/public';
  import AvailableCapacity from '$lib/components/Launch/AvailableCapacity.svelte';
  import type { Launch } from '$lib/types/public-resources/launches/launch';
  import cardOperator from '$lib/assets/img/launchId/cardOperator.png';
  import cardDateAndTime from '$lib/assets/img/launchId/cardDateAndTime.png';
  import type { Orbit } from '$lib/types/launch';

  export let data: import('./$types').PageData;

  let launchPublicResources: Launch;

  $: launch = data.launch;
  $: relatedLaunches = data.relatedLaunches;
  $: launchPublicResources = data.launchPublicResources;

  $: if (launch) {
    updateMetadata();
    prepareLaunchSiteGeoChartData();
  }

  let launchSiteGeoChartData: Array<Array<any>> = [];
  let pageTitle: string;
  let metaDescription: string | null;
  const geocharOptions = {
    backgroundColor: 'transparent',
    displayMode: 'markers',
    defaultColor: '#c6892a',
    colorAxis: { colors: ['#c95a02', '#c95a02'] },
    datalessRegionColor: '#bbbbbb',
    tooltip: { isHtml: false, trigger: 'none' },
    enableRegionInteractivity: false,
    region: 'world',
    legend: 'none',
    sizeAxis: {
      maxSize: 4
    }
  };

  /**
   * Updates page metadata such as title, description, etc.
   * WARNING: the code determining the various strings has been ported from the Angular legacy site and is completely untested!! Change it at your own risk.
   */
  function updateMetadata(): void {
    let tmpPageTitle = `${launch?.operator?.name} | ${launch?.vehicle?.name}`;
    let tmpMetaDescription = null;
    let date = formatDateFromString(launch?.readableDateAggregated!, 'UTC');
    if (launch?.isCompleted) {
      if (launch?.name !== null) {
        tmpMetaDescription = `The ${launch?.name} mission was launched by ${launch.operator?.name} ${launch.vehicle?.name} rocket on ${date}.`;
      } else {
        tmpMetaDescription = `A ${launch.operator?.name} ${launch.vehicle?.name} was launched on ${date}.`;
      }

      if (launch.site !== null) {
        tmpPageTitle = `${tmpPageTitle} | ${launch.site.name}`;
        tmpMetaDescription = `${tmpMetaDescription} The launch site used for this mission was ${launch.site.name}, ${launch.site.administrativeArea}, ${launch.site.country}.`;
      }

      if (launch?.name !== null) {
        tmpPageTitle = `${launch?.name} | ${tmpPageTitle}`;
      }
    } else {
      if (launch?.name !== null) {
        tmpMetaDescription = `A ${launch?.operator?.name} ${launch?.vehicle?.name} will launch the ${launch?.name} mission.`;
      } else {
        tmpMetaDescription = `A ${launch.operator?.name} ${launch.vehicle?.name} will be launched.`;
      }
      if (launch?.site !== null) {
        tmpPageTitle = `${tmpPageTitle} | ${launch?.site.name}`;
        tmpMetaDescription = `${tmpMetaDescription} The launch site is currently ${launch?.site.name}, ${launch?.site.administrativeArea}, ${launch?.site.country}.`;
      }
      if (launch?.name !== null) {
        tmpPageTitle = `${launch?.name} | ${tmpPageTitle}`;
      }
    }

    pageTitle = tmpPageTitle;
    metaDescription = tmpMetaDescription;
  }

  function prepareLaunchSiteGeoChartData() {
    launchSiteGeoChartData = [
      ['Latitude', 'Longitude', 'Label', 'size'],
      [
        launch.site?.latitude,
        launch.site?.longitude,
        `${launch.site?.name}, ${launch.site?.administrativeArea}, ${launch.site?.country}`,
        1
      ]
    ];
  }

  function capacityIsAvailable(): boolean {
    return (
      launchPublicResources.availableCapacity &&
      launchPublicResources.availableCapacity.slots.length > 0
    );
  }

  function formatOrbitTitle(orbitParameters: Orbit): string {
    const altitude = orbitParameters.altitude ? orbitParameters.altitude + 'km' : '';
    const inclination = orbitParameters.inclination ? orbitParameters.inclination + '°' : '';
    const comma = orbitParameters.altitude && orbitParameters.inclination ? ', ' : '';
    let title = `${humanizeOrbitType(orbitParameters.type)}`;

    if (orbitParameters.altitude || orbitParameters.inclination) {
      title += ` (${altitude}${comma}${inclination})`;
    }

    return title;
  }

  function formatVehicleOperator(name: string): string {
    return `Operated by: <strong><span style="margin-left: 0.25rem;">${name}</span></strong>`;
  }
</script>

<svelte:head>
  <title>{pageTitle}</title>
  <meta name="description" content={metaDescription} />
</svelte:head>

{#if !data.isLoading}
  <div class="container-fluid mt-5">
    <div class="container">
      <div class="row mb-5">
        <div class="row mb-2 justify-content-center">
          <div class="col-auto p-0 text-center d-flex align-items-center">
            <h2 class="d-inline">{launch.name ?? 'Unnamed Mission'}</h2>
          </div>

          {#if launch.type != null}
            <div class="col-auto d-flex align-items-center">
              <span class="badge rounded-pill bg-secondary">
                <Icon icon="tags" additionalClasses="me-2"></Icon>
                {humanizeLaunchType(launch.type)}
              </span>
            </div>
          {/if}
        </div>

        <div class="row justify-content-center">
          <div class="card-container col-lg-3 col-6 my-4">
            <LaunchInfoCard
              subtitle="LAUNCH WINDOW"
              title={formatDateFromString(launch.readableDateAggregated)}
              description={getFormatTime(launch.time)}
              image={cardDateAndTime}
              imageAlt="Drawing of a calendar and a clock"
              numberOfInjectedComponents={0}
            ></LaunchInfoCard>
          </div>

          <div class="card-container col-lg-3 col-6 my-4">
            {#if launch.secondaryOrbit}
              <LaunchInfoCard
                subtitle="PRIMARY ORBIT"
                title={formatOrbitTitle(launch.primaryOrbit)}
                numberOfInjectedComponents={2}
              >
                <OrbitParameters slot="header" orbitParameters={launch.primaryOrbit} />
                <OrbitParameters
                  slot="body"
                  isSecondaryOrbit={true}
                  secondaryOrbitTitle={formatOrbitTitle(launch.secondaryOrbit)}
                  orbitParameters={launch.secondaryOrbit}
                />
              </LaunchInfoCard>
            {:else}
              <LaunchInfoCard
                subtitle="ORBIT"
                title={formatOrbitTitle(launch.primaryOrbit)}
                image={cardOperator}
                imageAlt="a path of a rocket"
                numberOfInjectedComponents={1}
              >
                <OrbitParameters slot="header" orbitParameters={launch.primaryOrbit} />
              </LaunchInfoCard>
            {/if}
          </div>

          <div class="card-container col-lg-3 col-6 my-4">
            {#if launch.site && launchSiteGeoChartData}
              <LaunchInfoCard
                subtitle="LAUNCH SITE"
                title={launch.site?.name}
                description={`${launch.site?.administrativeArea}, ${launch.site?.country}`}
                numberOfInjectedComponents={1}
                ><GeoChart
                  slot="body"
                  dataChart={launchSiteGeoChartData}
                  options={geocharOptions}
                /></LaunchInfoCard
              >
            {:else}
              <LaunchInfoCard
                subtitle="LAUNCH SITE"
                title="N/D"
                image={cardLaunchSite}
                imageAlt="Drawing of a launch platform"
                numberOfInjectedComponents={0}
              ></LaunchInfoCard>
            {/if}
          </div>

          <div class="card-container col-lg-3 col-6 my-4">
            <LaunchInfoCard
              subtitle="VEHICLE"
              title={launch.vehicle.name}
              description={formatVehicleOperator(launch.operator.name)}
              image={launch.vehicle.existVehicleImage
                ? `${env.PUBLIC_LAUNCH_SERVICE_BASE_URL}vehicles/${launch.idVehicle}/image`
                : cardVehicle}
              imageAlt={launch.vehicle.existVehicleImage
                ? `Image of the ${launch.vehicle.name} rocket`
                : 'Drawn project of a Rocket'}
              numberOfInjectedComponents={0}
            ></LaunchInfoCard>
          </div>
        </div>
      </div>

      <div class="row mb-5 justify-content-center" id="availableCapacity">
        <div class="col-12 border-bottom mb-5">
          <h5>Available Capacity</h5>
        </div>
        <div class="col-12 col-md-9 col-lg-12 col-xl-11 col-xxl-10">
          {#if capacityIsAvailable()}
            <AvailableCapacity availableCapacity={launchPublicResources.availableCapacity}
            ></AvailableCapacity>
          {:else}
            <div class="row mt-5">
              <div class="ms-auto me-auto col-auto">
                <p>
                  There are no free slots availble for booking. Please contact us and let us know
                  your requirements. We'll be happy to assist you!
                </p>
              </div>
            </div>
            <div class="row mt-2 mb-5">
              <div class="ms-auto me-auto col-auto">
                <a href="/contactUs/bookYourLaunch">
                  <Button label="Book Your Launch">Book Your Launch</Button>
                </a>
              </div>
            </div>
          {/if}
        </div>
      </div>

      {#if relatedLaunches.length > 0}
        <div class="row mb-5">
          <div class="border-bottom">
            <h5>Related Launches</h5>
          </div>

          {#key relatedLaunches}
            <Carousel id={'relatedLaunches'} numberOfItems={relatedLaunches.length}>
              {#each relatedLaunches as relatedLaunch, index}
                <CarouselItem {index} numberOfItems={relatedLaunches.length}>
                  <LaunchCard launch={relatedLaunch}></LaunchCard>
                </CarouselItem>
              {/each}
            </Carousel>
          {/key}
        </div>
      {/if}
    </div>
  </div>
{/if}

<style>
  @media (max-width: 500px) {
    .card-container {
      width: 75%;
      margin: 0 12.5%;
    }
  }

  @media (max-width: 450px) {
    .card-container {
      width: 83%;
      margin: 0 8.5%;
    }
  }

  @media (max-width: 400px) {
    .card-container {
      width: 91%;
      margin: 0 4.5%;
    }
  }

  @media (max-width: 350px) {
    .card-container {
      width: 100%;
      margin: 0 0;
    }
  }
</style>
